import React, { useCallback } from 'react';
import { Button, Col, Form, FormProps, Row } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { HiOutlineCurrencyDollar } from 'react-icons/hi';

import cx from 'classnames';
import currencyFormat from 'utils/curencyFormat';

import Card from 'components/Card/Card';
import FormRadioButtonGroup from 'components/FormRadioButtonGroup/FormRadioButtonGroup';
import { IDonationOption } from 'models/donationOption';

export interface IFestiveBaubleDonationOptionsFormValues {
  donation_option_id?: number;
  custom_unit_amount?: number;
}
interface IFestiveBaubleDonationOptionsFormProps extends Omit<
  FormProps,
  'onSubmit' | 'onChange'
> {
  defaultValues?: IFestiveBaubleDonationOptionsFormValues;
  loading?: boolean;
  onSubmit?: (values: IFestiveBaubleDonationOptionsFormValues) => any;
  donationOptions?: IDonationOption[];
  onChange?: (values: IFestiveBaubleDonationOptionsFormValues) => any;
}
const FestiveBaubleDonationOptionsForm: React.FC<
  IFestiveBaubleDonationOptionsFormProps
> = ({
  onSubmit = () => {},
  defaultValues = {},
  donationOptions,
  loading,
  onChange,
  ...props
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
  } = useForm({
    defaultValues: {
      ...defaultValues,
      donation_option_id: donationOptions?.find(
        ({ id }) => id === defaultValues?.donation_option_id,
      ),
    },
  });

  const getFormattedValues = useCallback(() => {
    const values = getValues();
    return {
      custom_unit_amount: values.custom_unit_amount
        ? parseInt(values.custom_unit_amount.toString(), 10)
        : undefined,
      donation_option_id: values.donation_option_id?.id,
    };
  }, [getValues]);

  const beforeHandleSubmit = useCallback(() => {
    onSubmit(getFormattedValues());
  }, [getFormattedValues, onSubmit]);

  return (
    <Form onSubmit={handleSubmit(beforeHandleSubmit)} {...props}>
      <Card loading={loading} className="p-0 no-shadow align-items-start">
        {!!donationOptions?.length && (
          <Form.Group className="mt-0 mb-n2">
            <Controller
              control={control}
              render={({ field }) => (
                <FormRadioButtonGroup
                  {...field}
                  className={cx({
                    'is-invalid': errors?.donation_option_id,
                  })}
                  inline
                  onClick={() => {
                    if (onChange) {
                      // TODO: Hack since I don't have access to onChange once I use Controller
                      setTimeout(() => {
                        onChange(getFormattedValues());
                      });
                    }
                  }}
                  optionValueName="id"
                  optionLabelName="amount"
                  getLabelValue={(value: IDonationOption) =>
                    `${currencyFormat(value.amount).replace(/.00$/, '')}`
                  }
                  labelProps={{ className: 'ml-2 d-block mb-2 mr-3' }}
                  options={donationOptions}
                />
              )}
              name="donation_option_id"
              rules={{ required: true }}
            />
          </Form.Group>
        )}
        <Row className="align-items-center mt-2 mb-3 px-0">
          <Col lg={6} className="pr-md-0">
            <Form.Label
              className={cx(
                'text-center',
                'text-md-left',
                'd-block',
                // s['custom-donation-label'],
                'text-bold',
                'm-md-0',
                'mb-3',
              )}
            >
              Or choose a custom donation
            </Form.Label>
          </Col>
          <Col lg={6}>
            <div className="d-flex align-items-center">
              <HiOutlineCurrencyDollar
                size={32}
                className="text-primary mr-1"
              />
              <Form.Control
                type="number"
                placeholder="enter amount"
                min={1}
                className="px-1 square-amount-input"
                onChange={() => {
                  if (onChange) {
                    onChange(getFormattedValues());
                  }
                }}
                {...register('custom_unit_amount')}
              />
            </div>
          </Col>
        </Row>
        <Button type="submit" variant="danger" disabled={loading}>
          Next
        </Button>
      </Card>
    </Form>
  );
};

export default FestiveBaubleDonationOptionsForm;
